'use client';

import { createContext, useState, useEffect, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { stripLangPrefix } from '@/i18n/config';

type ActivePage = 'home' | 'about' | 'work' | 'blog' | 'contact' | 'imprint' | 'privacy' | 'cv' | null;

interface AppContextType {
  activePage: ActivePage;
  setActivePage: (page: ActivePage) => void;
  isOnCvSubdomain: boolean;
}

export const AppContext = createContext<AppContextType>({
                                                          activePage: null,
                                                          setActivePage: () => {},
                                                          isOnCvSubdomain: false
                                                        });

// Erkennt die aktive Seite aus dem URL-Pfad (pure Funktion, auch fürs SSR)
const pageFromPath = (rawPath: string): ActivePage => {
  const { path } = stripLangPrefix(rawPath);
  if (path === '/') return 'home';
  if (path.startsWith('/cv')) return 'cv';
  if (path.startsWith('/work')) return 'work';
  if (path.startsWith('/blog')) return 'blog';
  if (path.startsWith('/about')) return 'about';
  if (path.startsWith('/contact')) return 'contact';
  if (path.startsWith('/imprint')) return 'imprint';
  if (path.startsWith('/privacy')) return 'privacy';
  return null;
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  // Direkt aus dem Pfad initialisieren: so rendert schon das Server-HTML die
  // richtige Farbwelt (vorher: null -> schwarzer Default, nach Hydration
  // 1200ms-Gradient-Blende = sichtbares "Blitzen" auf hellen Seiten wie /blog)
  const [activePage, setActivePage] = useState<ActivePage>(() => pageFromPath(pathname || '/'));
  const [isOnCvSubdomain, setIsOnCvSubdomain] = useState<boolean>(false);

  const setActivePageFromPath = (rawPath: string) => {
    setActivePage(pageFromPath(rawPath));
  };

  // Automatisches Setzen der aktiven Seite beim initialen Laden und URL-Änderungen
  useEffect(() => {
    if (pathname) {
      setActivePageFromPath(pathname);
    }
  }, [pathname]);

  // Erkennung der CV-Subdomain
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isCvSubdomain = hostname === 'cv.localhost' || 
                           hostname === 'cv.localhost:3000' || 
                           hostname.startsWith('cv.');
      setIsOnCvSubdomain(isCvSubdomain);
    }
  }, []);

  // Effect für das Setzen des data-page-Attributs am body-Element
  useEffect(() => {
    if (activePage && typeof document !== 'undefined') {
      document.body.dataset.page = activePage;
    }
  }, [activePage]);

  return (
    <AppContext.Provider value={{ activePage, setActivePage, isOnCvSubdomain }}>
      {children}
    </AppContext.Provider>
  );
};